import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import HistorialPage from "@/pages/HistorialPage";
import NuevaSolicitudPage from "@/pages/NuevaSolicitudPage";
import AprobacionesPage from "@/pages/AprobacionesPage";
import CalendarioPage from "@/pages/CalendarioPage";
import AdminPage from "@/pages/AdminPage";
import EquipoDashboardPage from "@/pages/EquipoDashboardPage";
import ApprovalLinkPage from "@/pages/ApprovalLinkPage";
import Layout from "@/components/Layout";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import ConstanciasPage from "@/pages/ConstanciasPage";
import LlamadasAtencionPage from "@/pages/LlamadasAtencionPage";
import BoletosOrnatoPage from "@/pages/BoletosOrnatoPage";
import ManualesPage from "@/pages/ManualesPage";
import MarcajesPage from "@/pages/MarcajesPage";
import ComunicadosPage from "@/pages/ComunicadosPage";
import BeneficiosPage from "@/pages/BeneficiosPage";
import "@/App.css";

const ProtectedRoute = ({ children, allowedRoles, permiso }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.rol) && !(permiso && user?.[permiso])) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/aprobar" element={<ApprovalLinkPage />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="historial" element={<HistorialPage />} />
        <Route path="nueva-solicitud" element={<NuevaSolicitudPage />} />
        <Route
          path="aprobaciones"
          element={
            <ProtectedRoute allowedRoles={["supervisor", "administrador", "rrhh"]} permiso="puede_aprobar">
              <AprobacionesPage />
            </ProtectedRoute>
          }
        />
        <Route path="calendario" element={<CalendarioPage />} />
        <Route
          path="equipo-dashboard"
          element={
            <ProtectedRoute allowedRoles={["supervisor", "administrador", "rrhh"]}>
              <EquipoDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute allowedRoles={["administrador", "rrhh"]}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="empleados/:id"
          element={
            <ProtectedRoute allowedRoles={["supervisor", "administrador", "rrhh"]}>
              <EmployeeDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Nuevos Módulos */}
        <Route
          path="constancias"
          element={
            <ProtectedRoute allowedRoles={["empleado", "supervisor", "rrhh", "administrador"]}>
              <ConstanciasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="llamadas-atencion"
          element={
            <ProtectedRoute allowedRoles={["empleado", "supervisor", "rrhh", "administrador"]}>
              <LlamadasAtencionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="boletos-ornato"
          element={
            <ProtectedRoute allowedRoles={["empleado", "supervisor", "rrhh", "administrador"]}>
              <BoletosOrnatoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="manuales"
          element={
            <ProtectedRoute allowedRoles={["empleado", "supervisor", "rrhh", "administrador"]}>
              <ManualesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="marcajes"
          element={
            <ProtectedRoute allowedRoles={["rrhh", "administrador"]} permiso="puede_marcajes">
              <MarcajesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicados"
          element={
            <ProtectedRoute allowedRoles={["rrhh", "administrador"]}>
              <ComunicadosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="beneficios"
          element={
            <ProtectedRoute allowedRoles={["empleado", "supervisor", "rrhh", "administrador"]}>
              <BeneficiosPage />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
